import React, { useState, useEffect } from "react";
import { EnvironmentalData } from "@/services/environmentalDataService";
import { getSpeciesImage } from "@/lib/inaturalist";
import { getSpeciesDescription } from "@/lib/wikipedia";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  X,
  Fish,
  Scale,
  Map,
  FileText,
  TrendingDown,
  TrendingUp,
  MoveHorizontal,
  Circle,
} from "lucide-react";
import DataSourceInfo from "@/components/DataSourceInfo";
import { cn } from "@/lib/utils";

interface SpeciesTabProps {
  data: EnvironmentalData;
}

// Extended interface for species data
interface ExtendedSpecies {
  name: string;
  scientificName: string;
  status: "Critically Endangered" | "Endangered" | "Vulnerable";
  population: number | "Unknown";
  trend: "Decreasing" | "Stable" | "Increasing";
  habitats?: string[];
  description?: string;
}

interface SpeciesImage {
  [key: string]: {
    url?: string;
    attribution?: string;
    license?: string;
    error?: boolean;
    message?: string;
    fallbackImage?: string;
  } | null;
}

interface SpeciesDetails {
  [key: string]: {
    habitats?: string[];
    description?: string;
    conservationStatus?: string;
    wikipediaUrl?: string;
  };
}

const SpeciesTab: React.FC<SpeciesTabProps> = ({ data }) => {
  const [speciesImages, setSpeciesImages] = useState<SpeciesImage>({});
  const [speciesDetails, setSpeciesDetails] = useState<SpeciesDetails>({});
  const [loadingImages, setLoadingImages] = useState<{
    [key: string]: boolean;
  }>({});
  const [loadingDetails, setLoadingDetails] = useState<{
    [key: string]: boolean;
  }>({});
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    attribution: string;
    license: string;
    name: string;
  } | null>(null);

  // Get extended species data from API data
  const getExtendedSpeciesData = (species: any): ExtendedSpecies => {
    const details = speciesDetails[species.name];

    return {
      ...species,
      habitats: details?.habitats || ["Unknown"],
      description: details?.description,
    };
  };

  useEffect(() => {
    const loadResources = async () => {
      console.log(
        "Loading resources for all species:",
        data.endangeredSpecies.topThreatenedSpecies,
      );

      // Load images and details for all species
      for (const species of data.endangeredSpecies.topThreatenedSpecies) {
        console.log("Loading data for species:", species.name);

        // Get image from iNaturalist
        setLoadingImages((prev) => ({ ...prev, [species.name]: true }));
        const imageData = await getSpeciesImage(
          species.name,
          species.scientificName,
        );
        setSpeciesImages((prev) => ({ ...prev, [species.name]: imageData }));
        setLoadingImages((prev) => ({ ...prev, [species.name]: false }));

        // Get description from Wikipedia
        setLoadingDetails((prev) => ({ ...prev, [species.name]: true }));
        try {
          const details = await getSpeciesDescription(
            species.name,
            species.scientificName,
          );
          console.log(
            "Received species details for",
            species.name,
            ":",
            details,
          );
          setSpeciesDetails((prev) => ({
            ...prev,
            [species.name]: {
              description: details.description,
              wikipediaUrl: details.url,
            },
          }));
        } catch (error) {
          console.error("Error fetching species details:", error);
        } finally {
          setLoadingDetails((prev) => ({ ...prev, [species.name]: false }));
        }
      }
    };

    loadResources();
  }, [data.endangeredSpecies.topThreatenedSpecies]);

  const SpeciesStatusBadge = ({ status }: { status: string }) => {
    switch (status) {
      case "Critically Endangered":
        return (
          <span className="px-2 py-0.5 rounded-full bg-[#ff0000]/20 text-[#ff0000] text-xs font-medium whitespace-nowrap">
            Critically Endangered
          </span>
        );
      case "Endangered":
        return (
          <span className="px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-500 text-xs font-medium">
            Endangered
          </span>
        );
      case "Vulnerable":
        return (
          <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-500 text-xs font-medium">
            Vulnerable
          </span>
        );
      default:
        return null;
    }
  };

  const TrendIndicator = ({ trend }: { trend: string }) => {
    switch (trend) {
      case "Decreasing":
        return (
          <span className="flex items-center text-[#ff0000] text-xs">
            <TrendingDown className="w-3 h-3 mr-1" /> Decreasing
          </span>
        );
      case "Increasing":
        return (
          <span className="flex items-center text-green-600 text-xs">
            <TrendingUp className="w-3 h-3 mr-1" /> Increasing
          </span>
        );
      case "Stable":
        return (
          <span className="flex items-center text-blue-500 text-xs">
            <MoveHorizontal className="w-3 h-3 mr-1" /> Stable
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Hero Metrics Dashboard */}
      <div className="relative overflow-hidden rounded-xl border border-border bg-gradient-to-br from-background via-red-50/30 dark:via-red-950/20 to-orange-50/30 dark:to-orange-950/20 p-6">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-grid-16" />
        <div className="relative">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500/20 to-orange-500/20 flex items-center justify-center border border-red-500/20">
                <span className="text-xl">🦋</span>
              </div>
              <div>
                <h3 className="text-lg font-bold">
                  Species Conservation Status
                </h3>
                <p className="text-sm text-left text-muted-foreground">
                  Regional biodiversity overview
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-primary">
                {data.endangeredSpecies.count}
              </div>
              <p className="text-xs text-muted-foreground">
                Total species monitored
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 border border-border/50">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-sm font-medium text-muted-foreground">
                  Critical
                </span>
              </div>
              <div className="text-3xl font-bold text-red-500">
                {data.endangeredSpecies.criticallyEndangered}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Immediate action required
              </p>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 border border-border/50">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                <span className="text-sm font-medium text-muted-foreground">
                  Endangered
                </span>
              </div>
              <div className="text-3xl font-bold text-orange-500">
                {data.endangeredSpecies.endangered}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                High conservation priority
              </p>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 border border-border/50">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-sm font-medium text-muted-foreground">
                  Vulnerable
                </span>
              </div>
              <div className="text-3xl font-bold text-amber-500">
                {data.endangeredSpecies.vulnerable}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Declining populations
              </p>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 border-4 border-border/70">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-[.7rem] font-medium text-muted-foreground text-center w-full font-semibold">
                  Species Above Critical Threshold
                </span>
              </div>
              {/* Tooltip wrapper for conservation rate */}
              <div className="relative group">
                <div className="text-3xl font-bold text-primary cursor-help text-center">
                  {Math.round(
                    ((data.endangeredSpecies.count -
                      data.endangeredSpecies.criticallyEndangered) /
                      data.endangeredSpecies.count) *
                      100,
                  )}
                  %
                </div>
                <div className="absolute mr-10 right-0 -top-20 z-10 hidden group-hover:block bg-background border border-border rounded px-3 py-2 text-xs text-muted-foreground shadow-lg w-64 text-center">
                  Rate is calculated as:
                  <br />
                  <span className="font-semibold">
                    (Total species monitored - Critically Endangered species) /
                    Total species monitored × 100
                  </span>
                  <br />
                  This represents the percentage of species not at immediate
                  risk of extinction.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Species Gallery */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold">Priority Conservation Species</h3>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            {data.endangeredSpecies.topThreatenedSpecies.length} species
            profiled
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {data.endangeredSpecies.topThreatenedSpecies.map((species, idx) => {
            const extendedSpecies = getExtendedSpeciesData(species);
            return (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-xl border border-border bg-card hover:shadow-lg transition-all duration-300 hover:border-primary/30"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                {/* Species Image */}
                <div className="relative h-48 overflow-hidden">
                  {loadingImages[species.name] ? (
                    <div className="w-full h-full bg-muted animate-pulse flex items-center justify-center">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : speciesImages[species.name] ? (
                    <div className="relative w-full h-full">
                      <img
                        src={
                          speciesImages[species.name]?.url ||
                          "/species-fallback.png"
                        }
                        alt={species.name}
                        className="w-full h-full object-cover cursor-pointer group-hover:scale-105 transition-transform duration-300"
                        onClick={() =>
                          setSelectedImage({
                            url:
                              speciesImages[species.name]?.url ||
                              "/species-fallback.png",
                            attribution:
                              speciesImages[species.name]?.attribution || "",
                            license: speciesImages[species.name]?.license || "",
                            name: species.name,
                          })
                        }
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = "/species-fallback.png";
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      <div className="absolute top-4 right-4">
                        <SpeciesStatusBadge status={species.status} />
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-4xl mb-2">🐾</div>
                        <span className="text-sm text-muted-foreground">
                          No image available
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Species Information */}
                <div className="relative p-5 space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4
                          className={cn("text-lg font-bold", {
                            "text-red-600 dark:text-red-400 text-left":
                              species.status === "Critically Endangered",
                            "text-orange-600 dark:text-orange-400":
                              species.status === "Endangered",
                            "text-amber-600 dark:text-amber-400":
                              species.status === "Vulnerable",
                          })}
                        >
                          {/* Species Name */}
                          {species.name}
                        </h4>
                        {/* Scientific Name */}
                        <p className="text-sm text-left text-muted-foreground italic">
                          {species.scientificName}
                        </p>
                      </div>
                    </div>

                    {/* Description */}
                    {loadingDetails[species.name] ? (
                      <div className="space-y-2">
                        <div className="h-4 bg-muted rounded animate-pulse" />
                        <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                      </div>
                    ) : extendedSpecies.description ? (
                      <p className="text-sm text-left text-muted-foreground leading-relaxed line-clamp-2">
                        {extendedSpecies.description.split("\n")[0]}
                      </p>
                    ) : null}
                  </div>

                  {/* Species Metrics */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border/50">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Circle className="w-3 h-3 fill-primary/20 stroke-primary" />
                        Population
                      </div>
                      <div className="font-semibold">
                        {typeof species.population === "number"
                          ? species.population.toLocaleString()
                          : species.population}
                      </div>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <TrendIndicator trend={species.trend} />
                      </div>
                      <div className="font-semibold text-sm">
                        {species.trend}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 pt-2 border-t border-border/50 text-left justify-between">
                    {speciesDetails[species.name]?.wikipediaUrl && (
                      <a
                        href={speciesDetails[species.name].wikipediaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg text-sm font-medium transition-colors"
                      >
                        <FileText className="w-4 h-4" />
                        Learn More
                      </a>
                    )}
                    <button
                      onClick={() =>
                        setSelectedImage({
                          url:
                            speciesImages[species.name]?.url ||
                            "/species-fallback.png",
                          attribution:
                            speciesImages[species.name]?.attribution || "",
                          license: speciesImages[species.name]?.license || "",
                          name: species.name,
                        })
                      }
                      className="px-3 py-2 border border-border hover:border-primary/50 rounded-lg text-sm font-medium transition-colors"
                    >
                      View Image
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Image Modal */}
      <Dialog
        open={!!selectedImage}
        onOpenChange={(open) => !open && setSelectedImage(null)}
      >
        <DialogContent className="max-w-4xl p-0 z-[9999]">
          <div className="relative">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute right-4 top-4 z-10 rounded-full bg-black/60 p-2 text-white hover:bg-black/80 transition-colors"
              aria-label="Close image dialog"
            >
              <X className="h-5 w-5" />
            </button>
            <img
              src={selectedImage?.url}
              alt={selectedImage?.name}
              className="w-full rounded-lg object-contain max-h-[80vh]"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-white p-4 rounded-b-lg">
              <h3 className="font-semibold text-lg">{selectedImage?.name}</h3>
              <p className="text-sm text-gray-300 mt-1">
                {selectedImage?.attribution} ({selectedImage?.license})
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Data Source Information */}
      <DataSourceInfo
        dataType="species"
        updatedAt={data.updatedAt}
        dbSources={data.sources}
      />
    </div>
  );
};

export default SpeciesTab;
